import { useState } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { DiscoveryMediaType } from "@rago/web-shared";
import { discoverySource, toMediaItem, toSeasonMediaItem } from "../../sources/discoverySource";
import { discoveryGuid } from "../../lib/discoveryLocations";
import { useAmbientBackdrop } from "../../lib/pageBackdrop";
import { isOnWatchlist, useWatchlist, useWatchlistToggle } from "../../lib/watchlist";
import { markQuestion, regularEpisodes, useWatched, useWatchedToggle } from "../../lib/watched";
import { HubRow } from "../../components/media";
import { CastRow } from "../../components/library/cast-row";
import { DirectorsByline } from "../../components/library/directors-byline";
import { DetailHero } from "../../components/library/detail-hero";
import { SeasonsGrid } from "../../components/library/season-browser";
import { WatchLocations } from "../../components/library/watch-locations";
import { CheckIcon, PlayIcon, PlusIcon } from "../../components/icons";
import { LoadingState, NotFoundState } from "../../components/page-state";
import { ConfirmDialog } from "../../components/modal";

export const Route = createFileRoute("/_authed/discover/$mediaType/$tmdbId")({
  component: DiscoveryDetailPage,
});

/**
 * The Discovery detail page rides the same DetailHero as owned media — one
 * hero for the whole app; only the actions differ (Mark as Watched on the
 * account's own watch log, watchlist, trailer — instead of Play — plus the
 * "watch from these locations" cards in the text column: every online
 * server asked for the title as the page opens). A show's mark asks first,
 * Plex's way, since it marks every episode.
 */
function DiscoveryDetailPage() {
  // An art route: the shell's backdrop carries the last art seen while
  // nothing of ours has decoded (lib/pageBackdrop) — first hook, before any
  // early return, so the spinner phase has it too.
  useAmbientBackdrop();
  const { mediaType, tmdbId } = Route.useParams();
  const wl = useWatchlist();
  const { toggle, busyKey, error: wlError } = useWatchlistToggle();
  const valid = mediaType === "movie" || mediaType === "tv";
  const watched = useWatched(mediaType as DiscoveryMediaType, tmdbId, valid);
  const { setWatched, busy: markBusy, error: markError } = useWatchedToggle();
  const [askMark, setAskMark] = useState(false);

  const detail = useQuery({
    queryKey: ["discover-detail", mediaType, tmdbId],
    queryFn: async () => {
      if (!valid) throw notFound();
      return discoverySource.getDetail(mediaType as DiscoveryMediaType, tmdbId);
    },
    staleTime: 6 * 60 * 60 * 1000,
  });

  if (!valid) return <NotFoundState backTo="/discover" backLabel="Back to Discover" />;
  if (detail.isLoading) {
    return <LoadingState />;
  }
  if (detail.isError || !detail.data) {
    return <NotFoundState backTo="/discover" backLabel="Back to Discover" />;
  }

  const d = detail.data;
  const heroItem = toMediaItem(d);
  const saved = isOnWatchlist(wl.data, d.mediaType, d.id);
  const wlBusy = busyKey !== null;
  const isWatched = watched.data?.watched ?? false;
  const episodes = watched.data?.episodes?.total ?? regularEpisodes(d.seasons);
  const mark = async () => {
    const ok = await setWatched(d.mediaType, d.id, !isWatched);
    if (ok) setAskMark(false);
  };

  return (
    <DetailHero
      item={heroItem}
      source={discoverySource}
      byline={
        <DirectorsByline
          verb={d.mediaType === "movie" ? "Directed by" : "Created by"}
          directors={d.directors}
          fallback={d.director}
        />
      }
      score={d.voteAverage}
      actions={
        <>
          <button
            onClick={() => (d.mediaType === "tv" ? setAskMark(true) : void mark())}
            disabled={markBusy || watched.isLoading}
            className={`rago-btn rago-btn-quiet disabled:opacity-60 ${
              isWatched
                ? "border-[var(--rago-accent-line)] text-[var(--rago-accent)] hover:text-[var(--rago-accent-strong)]"
                : ""
            }`}
            data-testid="discover-watched"
            aria-pressed={isWatched}
          >
            {markBusy ? (
              "…"
            ) : (
              <>
                <CheckIcon size={16} className="-ml-1" />
                {isWatched ? "Watched" : "Mark watched"}
              </>
            )}
          </button>
          <ConfirmDialog
            open={askMark}
            title={isWatched ? "Mark as Unwatched" : "Mark as Watched"}
            body={<p>{markQuestion(d.title, episodes, isWatched)}</p>}
            confirmLabel="Yes"
            busy={markBusy}
            error={markError}
            onConfirm={() => void mark()}
            onCancel={() => setAskMark(false)}
          />
          <button
            onClick={() => void toggle(d.mediaType, d.id, saved)}
            disabled={wlBusy || wl.isLoading}
            className={`rago-btn rago-btn-quiet disabled:opacity-60 ${
              saved
                ? "border-[var(--rago-accent-line)] text-[var(--rago-accent)] hover:text-[var(--rago-accent-strong)]"
                : ""
            }`}
          >
            {wlBusy ? (
              "…"
            ) : saved ? (
              <>
                <CheckIcon size={16} className="-ml-1" />
                On Watchlist
              </>
            ) : (
              <>
                <PlusIcon size={16} className="-ml-1" />
                Watchlist
              </>
            )}
          </button>
          {d.trailerUrl ? (
            <a
              href={d.trailerUrl}
              target="_blank"
              rel="noreferrer"
              className="rago-btn rago-btn-quiet"
            >
              <PlayIcon size={16} className="-ml-1" />
              Trailer&nbsp;↗
            </a>
          ) : null}
          {wlError ? (
            <span className="text-xs text-[var(--rago-error)]">
              Couldn&apos;t update your watchlist. Try again.
            </span>
          ) : null}
          {markError && !askMark ? (
            <span className="text-xs text-[var(--rago-error)]">
              Couldn&apos;t mark it. Try again.
            </span>
          ) : null}
        </>
      }
      below={
        <div className="rago-detail-below">
          {/* Same section, size and gutter as the library show page: season
              cards deep-link to /discover/tv/{id}/season/{n}. */}
          {d.mediaType === "tv" && d.seasons.length > 0 ? (
            <div className="px-6 sm:px-10">
              <SeasonsGrid
                source={discoverySource}
                seasons={d.seasons.map((s) => toSeasonMediaItem(s, d.id))}
              />
            </div>
          ) : null}
          <CastRow detail={d} />
          <HubRow
            hub={{
              id: "recommendations",
              title: "More Like This",
              items: d.recommendations.map(toMediaItem),
            }}
            source={discoverySource}
            caption="From Discovery"
          />
        </div>
      }
    >
      {/* Every online server is asked for this title as the page opens —
          one card per copy, Plex's availability row. */}
      <WatchLocations guid={discoveryGuid(d.mediaType, d.id)} />
    </DetailHero>
  );
}
